import { AxiosError, AxiosProgressEvent, AxiosResponse, CancelTokenSource } from 'axios';
import { action, observable, runInAction, makeObservable } from 'mobx';

import DetailsActivities from 'ui/stores/DetailsActivities';
import { ActivityListResponse, ActivityResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';

import API from 'utils/api';
import RootStore from 'stores/Root';
import Logger from 'utils/logger';

import { PaginationParams } from 'utils/api/types';
import { UserSearchResponse } from 'utils/api/modules/user/user.types';
import {
  QuestionActivityHost,
  QuestionActivityStats,
  QuestionActivityStatsResponse,
  QuestionLink,
  QuestionTaskLinkTask,
} from 'utils/api/modules/entities/entities.types';

export const EMPTY_STATS: QuestionActivityStats = { comments: 0, files: 0, tasks: 0, links: 0 };

export type AuditQuestionActivitiesTarget = {
  entityType: string;
  host: QuestionActivityHost;
  hostId: string;
};

/** One instance serves a whole form: `stats` covers every question, so a section render costs one
 * request; the feed and links follow whichever question the drawer is open on. */
export default class AuditQuestionActivities extends DetailsActivities<RootStore, typeof API> {
  entityType: string;

  host: QuestionActivityHost;

  hostId: string;

  @observable questionKey = '';

  @observable stats: QuestionActivityStatsResponse = {};

  @observable links?: QuestionLink[];

  @observable linksLoading = false;

  @observable tasks?: QuestionTaskLinkTask[];

  @observable tasksLoading = false;

  @observable tasksFailed = false;

  /** `createLink`'s channel — rendered inline on the picker. Load failures toast instead. */
  @observable linkError?: string;

  @observable linksFailed = false;

  @observable statsLoaded = false;

  constructor(rootStore: RootStore, api: typeof API, target: AuditQuestionActivitiesTarget) {
    super(rootStore, api);

    makeObservable(this);

    this.entityType = target.entityType;
    this.host = target.host;
    this.hostId = target.hostId;
  }

  /** Bumped on every question switch. One store outlives the drawer, so an in-flight read has to be
   * able to tell that its question is no longer the one on screen. */
  private generation = 0;

  @action.bound
  setQuestion(questionKey: string): void {
    if (this.questionKey === questionKey) {
      return;
    }

    this.generation += 1;
    this.questionKey = questionKey;
    this.reset();
    this.error = undefined;
    this.hasMore = true;
    this.links = undefined;
    this.linkError = undefined;
    this.linksFailed = false;
    this.tasks = undefined;
    this.tasksFailed = false;
  }

  @action.bound
  async loadFeed(): Promise<void> {
    const generation = this.generation;
    await this.loadActivities();

    // Switched question mid-request: the base wrote the old question's page and its isLoading guard
    // dropped the new question's load, so clear it and let the tab's effect re-read.
    if (generation !== this.generation) {
      this.reset();
      this.error = undefined;
      this.hasMore = true;
      return;
    }

    if (this.error) {
      this.rootStore.notification.enqueueErrorSnackbar('Unable to load activity for this question');
    }
  }

  /** Switches the question first: React runs child effects before the parent's, so a tab loading on
   * its own would otherwise fetch under the previous question's key. */
  @action.bound
  async ensureFeed(questionKey: string): Promise<void> {
    this.setQuestion(questionKey);

    if (this.activities === undefined && !this.isLoading && !this.error) {
      await this.loadFeed();
    }
  }

  @action.bound
  async ensureLinks(questionKey: string): Promise<void> {
    this.setQuestion(questionKey);

    if (this.links === undefined && !this.linksLoading && !this.linksFailed) {
      await this.loadLinks();
    }
  }

  @action.bound
  async ensureTasks(questionKey: string): Promise<void> {
    this.setQuestion(questionKey);

    if (this.tasks === undefined && !this.tasksLoading && !this.tasksFailed) {
      await this.loadTasks();
    }
  }

  /** Re-read the feed from the first page. `reloadActivities` appends from `activities.length`, so
   * after an insert it fetches past the end of a list that just grew at the front. */
  @action.bound
  async refreshFeed(): Promise<void> {
    this.reset();
    this.error = undefined;
    this.hasMore = true;
    await this.loadFeed();
  }

  // The store outlives the drawer, so a failure flag survives close-and-reopen; these are the only
  // way back out of an error state.
  @action.bound
  async retryFeed(): Promise<void> {
    this.error = undefined;
    await this.loadFeed();
  }

  @action.bound
  async retryLinks(): Promise<void> {
    this.linksFailed = false;
    await this.loadLinks();
  }

  @action.bound
  async retryTasks(): Promise<void> {
    this.tasksFailed = false;
    await this.loadTasks();
  }

  statsFor(questionKey: string): QuestionActivityStats {
    return this.stats[questionKey] || EMPTY_STATS;
  }

  async loadActivitiesData(params: PaginationParams): Promise<AxiosResponse<ActivityListResponse>> {
    return this.api.entities.loadQuestionActivities(
      this.entityType,
      this.host,
      this.hostId,
      this.questionKey,
      params
    )();
  }

  @action.bound
  async loadStats(): Promise<void> {
    try {
      const { data } = await this.api.entities.loadQuestionActivityStats(this.entityType, this.host, this.hostId)();
      runInAction(() => {
        this.stats = data;
        this.statsLoaded = true;
      });
    } catch (e) {
      Logger.error(`Invalid question activity stats response: host ${this.hostId}`, e);
    }
  }

  @action.bound
  private bumpStat(kind: keyof QuestionActivityStats, delta: number): void {
    const current = this.statsFor(this.questionKey);
    this.stats = {
      ...this.stats,
      [this.questionKey]: { ...current, [kind]: Math.max(0, current[kind] + delta) },
    };
  }

  @action.bound
  async addMessage(message: string, taggedUsers?: UserSearchResponse[]): Promise<void> {
    try {
      const { data } = await this.api.entities.addQuestionComment(
        this.entityType,
        this.host,
        this.hostId,
        message,
        this.questionKey,
        taggedUsers
      )();
      runInAction(() => {
        this.activities = [...(this.activities || []), { ...data, type: ActivityType.Message }];
        this.bumpStat('comments', 1);
      });
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar('Unable to add the comment. Please try again later');
      Logger.error(`Invalid add question comment response: question ${this.questionKey}`, e);
    }
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    try {
      const { data } = await this.api.entities.uploadQuestionFile(
        this.entityType,
        this.host,
        this.hostId,
        file,
        this.questionKey,
        onUpdateProgress,
        cancelTokenSource
      )();
      runInAction(() => {
        this.activities = [...(this.activities || []), data as unknown as ActivityResponse];
        this.bumpStat('files', 1);
      });
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar('Failed to upload file');
      Logger.error(`Invalid question file upload response: question ${this.questionKey}`, e);
    }
  }

  @action.bound
  async deleteMessage(messageId: string): Promise<void> {
    try {
      await this.api.entities.deleteQuestionComment(this.entityType, this.host, this.hostId, messageId)();
      runInAction(() => {
        this.removeActivity(messageId);
        this.bumpStat('comments', -1);
      });
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar(
        'Unable to delete the comment at this time. Please try again later'
      );
      Logger.error(`Invalid delete question comment response: comment ${messageId}`, e);
    }
  }

  @action.bound
  async deleteFile(fileId: string): Promise<void> {
    try {
      await this.api.entities.deleteQuestionFile(this.entityType, this.host, this.hostId, fileId)();
      runInAction(() => {
        this.removeActivity(fileId);
        this.bumpStat('files', -1);
      });
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar(
        'Unable to delete the file at this time. Please try again later'
      );
      Logger.error(`Invalid delete question file response: file ${fileId}`, e);
    }
  }

  @action.bound
  async loadLinks(): Promise<void> {
    const generation = this.generation;
    this.linksLoading = true;
    this.linksFailed = false;
    try {
      const collected: QuestionLink[] = [];
      let offset = 0;
      let hasMore = true;

      // the endpoint pages at 20, so follow `next` rather than trusting one page
      while (hasMore) {
        const { data } = await this.api.entities.loadQuestionLinks(
          this.entityType,
          this.host,
          this.hostId,
          this.questionKey,
          { offset, limit: 20 }
        )();
        collected.push(...data.results);
        hasMore = !!data.next;
        offset += data.results.length;
        if (data.results.length === 0) {
          hasMore = false;
        }
      }

      // Discarded rather than written when the question moved on; the tab's effect re-reads.
      if (generation === this.generation) {
        runInAction(() => {
          this.links = collected;
        });
      }
    } catch (e) {
      // Terminal flag: an abandoned question's rejection must not block the one now on screen.
      if (generation === this.generation) {
        runInAction(() => {
          this.linksFailed = true;
        });
        this.rootStore.notification.enqueueErrorSnackbar('Unable to load related records. Please try again later');
      }
      Logger.error(`Invalid question links response: question ${this.questionKey}`, e);
    } finally {
      runInAction(() => {
        this.linksLoading = false;
      });
    }
  }

  /** The question's own actions, off the endpoint the badge counts. */
  @action.bound
  async loadTasks(): Promise<void> {
    const generation = this.generation;
    this.tasksLoading = true;
    this.tasksFailed = false;
    try {
      const collected: QuestionTaskLinkTask[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data } = await this.api.entities.loadQuestionTaskLinks(
          this.entityType,
          this.host,
          this.hostId,
          this.questionKey,
          { offset, limit: 20 }
        )();
        collected.push(...data.results.map((link) => link.task));
        hasMore = !!data.next;
        offset += data.results.length;
        if (data.results.length === 0) {
          hasMore = false;
        }
      }

      // Discarded rather than written when the question moved on; the tab's effect re-reads.
      if (generation === this.generation) {
        runInAction(() => {
          this.tasks = collected;
        });
      }
    } catch (e) {
      // Terminal flag: an abandoned question's rejection must not block the one now on screen.
      if (generation === this.generation) {
        runInAction(() => {
          this.tasksFailed = true;
        });
      }
      Logger.error(`Invalid question task links response: question ${this.questionKey}`, e);
    } finally {
      runInAction(() => {
        this.tasksLoading = false;
      });
    }
  }

  @action.bound
  clearLinkError(): void {
    this.linkError = undefined;
  }

  @action.bound
  async createLink(entityUuid: string): Promise<boolean> {
    this.linkError = undefined;
    try {
      const { data } = await this.api.entities.createQuestionLink(
        this.entityType,
        this.host,
        this.hostId,
        this.questionKey,
        entityUuid
      )();
      runInAction(() => {
        this.links = [data, ...(this.links || [])];
        this.bumpStat('links', 1);
      });
      return true;
    } catch (e) {
      const error = e as AxiosError<{ entity?: string[] | string; detail?: string }>;
      const body = error.response?.data;
      const entityError = Array.isArray(body?.entity) ? body?.entity[0] : body?.entity;

      runInAction(() => {
        this.linkError =
          entityError ||
          (error.response?.status === 404
            ? 'That record no longer exists, or you do not have access to it'
            : 'Unable to link the record. Please try again later');
      });
      return false;
    }
  }

  @action.bound
  async deleteLink(linkUuid: string): Promise<void> {
    try {
      await this.api.entities.deleteQuestionLink(this.entityType, this.host, this.hostId, linkUuid)();
      runInAction(() => {
        this.links = (this.links || []).filter((link) => link.uuid !== linkUuid);
        this.bumpStat('links', -1);
      });
    } catch (e) {
      this.rootStore.notification.enqueueErrorSnackbar('Unable to remove the related record. Please try again later');
      Logger.error(`Invalid delete question link response: link ${linkUuid}`, e);
    }
  }
}
